import { InputHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'
import { AlertCircle, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from '../../core/hooks/useTranslation'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  showPasswordToggle?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    label, 
    error, 
    helperText, 
    icon,
    iconPosition = 'left',
    showPasswordToggle,
    type,
    id, 
    ...props 
  }, ref) => {
    const { isRTL } = useTranslation()
    const [showPassword, setShowPassword] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`
    
    const inputType = showPasswordToggle && type === 'password' 
      ? (showPassword ? 'text' : 'password')
      : type

    const hasLeftIcon = icon && iconPosition === 'left'
    const hasRightIcon = icon && iconPosition === 'right'
    const hasPasswordToggle = showPasswordToggle && type === 'password'

    return (
      <div className="space-y-2">
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-danger-500 mr-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {hasLeftIcon && (
            <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none z-10`}>
              <span className={clsx(
                'text-gray-400',
                isRTL && type === 'tel' && 'scale-x-[-1]'
              )}>{icon}</span>
            </div>
          )}
          
          <div className={clsx(
            'relative',
            type === 'tel' && isRTL && 'flex justify-end'
          )}>
            <input
              id={inputId}
              type={inputType}
              dir={props.dir || (type === 'tel' || type === 'phone' ? 'ltr' : undefined)}
              className={clsx(
                'input',
                error && 'input-error',
                hasLeftIcon && (isRTL ? 'pr-10' : 'pl-10'),
                (hasRightIcon || hasPasswordToggle) && (isRTL ? 'pl-10' : 'pr-10'),
                type === 'tel' && isRTL && 'text-right',
                className
              )}
              ref={ref}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              {...props}
            />
          </div>
          
          {hasRightIcon && !hasPasswordToggle && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center pointer-events-none`}>
              <span className="text-gray-400">{icon}</span>
            </div>
          )}
          
          {hasPasswordToggle && (
            <button
              type="button"
              className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center text-gray-400 hover:text-gray-600 transition-colors`}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          )}
          
          {error && (
            <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center pointer-events-none`}>
              <AlertCircle className="w-5 h-5 text-danger-500" />
            </div>
          )}
        </div>
        
        {error && (
          <div className="flex items-center space-x-1 rtl:space-x-reverse">
            <AlertCircle className="w-4 h-4 text-danger-500 flex-shrink-0" />
            <p className="text-sm text-danger-600 font-medium">{error}</p>
          </div>
        )}
        
        {helperText && !error && (
          <p className="text-sm text-secondary-500">{helperText}</p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
